import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import type { ReactNode } from 'react'

interface RouteGuardProps {
  children: ReactNode
  allowedRoles: UserRoleEnum[]
  fallback?: ReactNode
}

/**
 * RouteGuard component that checks if the current user has permission to access a specific route
 * Shows 403/Unauthorized page instead of logging out the user
 */
export const RouteGuard = ({ children, allowedRoles, fallback }: RouteGuardProps) => {
  const { user } = useUserStore()

  // If user is not loaded yet, show loading
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Check if user role is allowed for this route
  const isAuthorized = allowedRoles.includes(user.userRole as UserRoleEnum)

  if (!isAuthorized) {
    // Show fallback component or default unauthorized page
    return fallback || <UnauthorizedPage />
  }

  return <>{children}</>
}

/**
 * Default Unauthorized component shown when user doesn't have access to a route
 */
const UnauthorizedPage = () => {
  const { user } = useUserStore()

  return (
    <div className="min-h-screen flex items-start justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-100 dark:bg-red-900/20 rounded-full">
          <svg
            className="w-8 h-8 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h1>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You don't have permission to access this page. Your current role ({user?.userRole})
          doesn't allow access to this resource.
        </p>

        <div className="space-y-3">
          <button
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            onClick={() => window.history.back()}
          >
            Go Back
          </button>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            If you believe this is an error, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  )
}

export default RouteGuard
